import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BarcodesTemplates } from 'src/app/barcode-print/barcode-print.component';
import { LabpartnerService } from 'src/app/services/labpartner.service';
import { Detail, User } from 'src/app/services/labpartner.service.model';
import { PAGE_NAME_PRINTRUNSHEET } from 'src/app/services/logging-constants';
import { LoggingService } from 'src/app/services/logging.service';
import { UserAccountService } from 'src/app/services/user-account.service';
import { NotificationService } from 'src/app/shared/notification.service';
import { LoadBarcodes } from './LoadBarcodes';

interface PrintColumn {
  header: string;
  dropdownHeader: string;
  index: number;
  visible: boolean;
}

const DEFAULT_COLUMNS: PrintColumn[] = [
  {
    header: 'Sample',
    dropdownHeader: 'Sample',
    index: 0,
    visible: true,
  },
  {
    header: 'Condition',
    dropdownHeader: 'Condition',
    index: 1,
    visible: true,
  },
  {
    header: 'Replicate #',
    dropdownHeader: 'Replicate #',
    index: 2,
    visible: true,
  },
  {
    header: 'Barcode',
    dropdownHeader: 'Barcode [string]',
    index: 3,
    visible: true,
  },
  {
    header: '',
    dropdownHeader: 'Barcode [graphic]',
    index: 4,
    visible: true,
  },
];

@Component({
  selector: 'app-detail-list-print',
  templateUrl: './detail-list-print.component.html',
  styleUrls: ['./detail-list-print.component.scss'],
  standalone: false,
})
export class DetailListPrintComponent extends LoadBarcodes implements OnInit {
  @ViewChild('columnSearchInput') columnSearchInput?: ElementRef<HTMLInputElement>;

  BarcodesTemplates = BarcodesTemplates;
  experimentId: number = 0;
  experimentLabel: string = '';
  detailsToPrint: Detail[] = [];
  detailIDs: number[] = [];
  isFilteredList: boolean = false;
  displayedColumns: string[] = ['sampleName', 'conditionName', 'replicateNo', 'barcode'];
  tableStateKey: string = '';
  tableConfig = {
    columns: this.createDefaultColumns(),
  };
  selectedColumns: PrintColumn[] = this.getVisibleColumns();
  columnSearchTerm: string = '';
  readonly compareColumns = (firstColumn: PrintColumn | null, secondColumn: PrintColumn | null) =>
    firstColumn?.dropdownHeader === secondColumn?.dropdownHeader;
  currentUser?: User;

  get columnVisibilityOptions(): PrintColumn[] {
    return [...this.tableConfig.columns].sort((a, b) => a.index - b.index);
  }

  get filteredColumnVisibilityOptions(): PrintColumn[] {
    const normalizedSearchTerm = this.columnSearchTerm.trim().toLowerCase();

    if (!normalizedSearchTerm) {
      return this.columnVisibilityOptions;
    }

    return this.columnVisibilityOptions.filter(column => {
      return (
        column.dropdownHeader.toLowerCase().includes(normalizedSearchTerm) ||
        column.header.toLowerCase().includes(normalizedSearchTerm)
      );
    });
  }

  get areAllFilteredColumnsSelected(): boolean {
    return (
      this.filteredColumnVisibilityOptions.length > 0 &&
      this.filteredColumnVisibilityOptions.every(column => column.visible)
    );
  }

  get areSomeFilteredColumnsSelected(): boolean {
    return (
      this.filteredColumnVisibilityOptions.some(column => column.visible) &&
      !this.areAllFilteredColumnsSelected
    );
  }

  constructor(
    private apiService: LabpartnerService,
    public accountService: UserAccountService,
    private route: ActivatedRoute,
    private loggingService: LoggingService,
    public notificationService: NotificationService,
  ) {
    super(notificationService);
  }

  ngOnInit(): void {
    this.experimentId = Number(this.route.snapshot.paramMap.get('id'));
    this.experimentLabel = this.apiService.padLeadingZeros(this.experimentId, 4);

    console.log('printing id ' + this.experimentId);

    const param = this.route.snapshot.queryParamMap.get('ids');
    if (param != undefined) {
      this.detailIDs = param.split('|').map(x => parseInt(x));
      this.isFilteredList = true;
    }

    this.renderPrintableList();

    this.loggingService.logPageView(PAGE_NAME_PRINTRUNSHEET);

    this.accountService.currentUser$.subscribe(user => {
      this.currentUser = user;
      if (!user) {
        return;
      }
      this.tableStateKey = 'Run_Sheet_' + this.currentUser?.userId + '_' + this.experimentId;
      this.loadTable();
    });
  }

  protected ngOnDestroyInternal(): void {
    // required by base component. clean up any component specific resources
  }

  private renderPrintableList() {
    this.subscription.add(
      this.apiService
        .getDetailsList('experimentId', this.experimentId)
        .subscribe((details: Detail[]) => {
          if (details.length == 0) {
            // TODO: show message that we didnt find any details
            return;
          }

          this.apiService.detailsDefaultSort(details);

          if (this.isFilteredList) {
            // filter the list of details to only those that are passed in on the query string
            this.detailsToPrint = details.filter(d => {
              return this.detailIDs.includes(d.detailId);
            });
          } else {
            this.detailsToPrint = details;
            this.barcodes = this.detailIDs.map(() => '');
          }
          this.isLoading = true;
          this.barcodes = this.detailsToPrint.map(() => '');
        }),
    );
  }

  onColumnSelectionChange() {
    const selectedColumns = new Set(this.selectedColumns.map(column => column.dropdownHeader));

    this.tableConfig.columns.forEach(c => {
      c.visible = selectedColumns.has(c.dropdownHeader);
    });
    this.selectedColumns = this.getVisibleColumns();
    this.saveTable();
  }

  onColumnSelectionOpenedChange(isOpen: boolean) {
    if (isOpen) {
      setTimeout(() => this.columnSearchInput?.nativeElement.focus());
      return;
    }

    this.columnSearchTerm = '';
  }

  onColumnSearchKeydown(event: KeyboardEvent) {
    event.stopPropagation();
  }

  onColumnSearchInteraction(event: Event) {
    event.stopPropagation();
  }

  toggleFilteredColumns(checked: boolean) {
    const filteredColumnHeaders = new Set(
      this.filteredColumnVisibilityOptions.map(column => column.dropdownHeader),
    );

    this.tableConfig.columns.forEach(column => {
      if (filteredColumnHeaders.has(column.dropdownHeader)) {
        column.visible = checked;
      }
    });

    this.selectedColumns = this.getVisibleColumns();
    this.saveTable();
  }

  clearColumnSearch(event?: Event) {
    event?.stopPropagation();
    this.columnSearchTerm = '';
    setTimeout(() => this.columnSearchInput?.nativeElement.focus());
  }

  saveTable() {
    if (!this.tableStateKey) {
      return;
    }

    window.localStorage.setItem(this.tableStateKey, JSON.stringify(this.tableConfig.columns));
  }

  loadTable() {
    const tableString = window.localStorage.getItem(this.tableStateKey);
    this.tableConfig.columns = this.hydrateColumns(tableString ? JSON.parse(tableString) : []);
    this.selectedColumns = this.getVisibleColumns();
  }

  moveColumnRight(index: number) {
    const col1 = this.selectedColumns[index];
    const col2 = this.selectedColumns[index + 1];

    if (!col1 || !col2) {
      return;
    }

    const tmp = col1.index;
    col1.index = col2.index;
    col2.index = tmp;

    this.selectedColumns = this.getVisibleColumns();
    this.saveTable();
  }

  moveColumnLeft(index: number) {
    const col1 = this.selectedColumns[index];
    const col2 = this.selectedColumns[index - 1];

    if (!col1 || !col2) {
      return;
    }

    const tmp = col1.index;
    col1.index = col2.index;
    col2.index = tmp;

    this.selectedColumns = this.getVisibleColumns();
    this.saveTable();
  }

  private createDefaultColumns(): PrintColumn[] {
    return DEFAULT_COLUMNS.map(column => ({ ...column }));
  }

  private getVisibleColumns(): PrintColumn[] {
    return this.columnVisibilityOptions.filter(column => column.visible);
  }

  private hydrateColumns(savedColumns: Partial<PrintColumn>[]): PrintColumn[] {
    const defaultColumns = this.createDefaultColumns();

    if (!savedColumns.length) {
      return defaultColumns;
    }

    const savedColumnsByHeader = new Map(
      savedColumns
        .filter((column): column is Partial<PrintColumn> & { dropdownHeader: string } => {
          return typeof column.dropdownHeader === 'string';
        })
        .map(column => [column.dropdownHeader, column]),
    );

    return defaultColumns.map(column => {
      const savedColumn = savedColumnsByHeader.get(column.dropdownHeader);

      if (!savedColumn) {
        return column;
      }

      return {
        ...column,
        header: typeof savedColumn.header === 'string' ? savedColumn.header : column.header,
        index: typeof savedColumn.index === 'number' ? savedColumn.index : column.index,
        visible: typeof savedColumn.visible === 'boolean' ? savedColumn.visible : column.visible,
      };
    });
  }
}
